import { FilterBase, RangeFilter } from '../../../../data/schema/SubstrateSearchRequest';
import AndType from 'owa-service/lib/contract/And';
import Constant from 'owa-service/lib/contract/Constant';
import ExecuteSearchJsonRequest from 'owa-service/lib/contract/ExecuteSearchJsonRequest';
import IsGreaterThanOrEqualTo from 'owa-service/lib/contract/IsGreaterThanOrEqualTo';
import IsLessThanOrEqualTo from 'owa-service/lib/contract/IsLessThanOrEqualTo';
import PropertyUri from 'owa-service/lib/contract/PropertyUri';
import RestrictionType from 'owa-service/lib/contract/RestrictionType';

/**
 * Creates DateTime range filters from SearchRestrictions (in ExecuteSearch request).
 */
export function createDateTimeRangeFilterFromExecuteSearchRequest(
    request: ExecuteSearchJsonRequest
) {
    const rangeFilters: FilterBase[] = [];

    const bounds = parseDateTimeRangeSearchRestrictions(request.Body.SearchRestrictions);
    const { lteFieldValue, gteFieldValue } = bounds;
    let { lteFieldUri, gteFieldUri } = bounds;

    if (lteFieldValue && lteFieldUri && gteFieldValue && gteFieldUri) {
        lteFieldUri = convertFieldUriToRangeFilterName(lteFieldUri);
        gteFieldUri = convertFieldUriToRangeFilterName(gteFieldUri);

        return createDateTimeRangeFilter(lteFieldValue, gteFieldValue, lteFieldUri, gteFieldUri);
    }

    return rangeFilters;
}

/**
 * Creates DateTime range filters from SearchRestrictions (in ExecuteSearch request).
 */
export default function createDateTimeRangeFilter(
    lteFieldValue: string,
    gteFieldValue: string,
    lteFieldUri: string,
    gteFieldUri: string
) {
    const rangeFilters: FilterBase[] = [];

    if (gteFieldUri === lteFieldUri) {
        /**
         * For Mail, the LTE and GTE range bounds are passed with "received"
         * range filter.
         */
        const rangeFilter: RangeFilter = {
            Range: {
                [gteFieldUri]: {
                    gte: gteFieldValue,
                    lte: lteFieldValue,
                },
            },
        };

        rangeFilters.push(rangeFilter);
    } else {
        /**
         * For Calendar, the LTE and GTE range bounds are passed with
         * "startTime" and "endTime" range filters.
         */
        const gteRangeFilter: RangeFilter = {
            Range: {
                [gteFieldUri]: {
                    gte: gteFieldValue,
                },
            },
        };
        rangeFilters.push(gteRangeFilter);

        const lteRangeFilter: RangeFilter = {
            Range: {
                [lteFieldUri]: {
                    lte: lteFieldValue,
                },
            },
        };
        rangeFilters.push(lteRangeFilter);
    }

    return rangeFilters;
}

function parseDateTimeRangeSearchRestrictions(searchRestrictions: RestrictionType) {
    let lteFieldValue: string = null;
    let lteFieldUri: string = null;
    let gteFieldValue: string = null;
    let gteFieldUri: string = null;

    if (searchRestrictions && searchRestrictions.Item) {
        const expression = searchRestrictions.Item;

        if (expression.__type === 'And:#Exchange') {
            const andExpression = expression as AndType;

            if (andExpression.Items && andExpression.Items.length === 2) {
                if (andExpression.Items[0].__type === 'IsLessThanOrEqualTo:#Exchange') {
                    const lteExpression = andExpression.Items[0] as IsLessThanOrEqualTo;

                    if (
                        lteExpression.FieldURIOrConstant.Item.__type === 'Constant:#Exchange' &&
                        lteExpression.Item.__type === 'PropertyUri:#Exchange'
                    ) {
                        const constant = lteExpression.FieldURIOrConstant.Item as Constant;
                        const propertyUri = lteExpression.Item as PropertyUri;

                        lteFieldValue = constant.Value;
                        lteFieldUri = propertyUri.FieldURI;
                    }
                }

                if (andExpression.Items[1].__type === 'IsGreaterThanOrEqualTo:#Exchange') {
                    const gteExpression = andExpression.Items[1] as IsGreaterThanOrEqualTo;

                    if (
                        gteExpression.FieldURIOrConstant.Item.__type === 'Constant:#Exchange' &&
                        gteExpression.Item.__type === 'PropertyUri:#Exchange'
                    ) {
                        const constant = gteExpression.FieldURIOrConstant.Item as Constant;
                        const propertyUri = gteExpression.Item as PropertyUri;

                        gteFieldValue = constant.Value;
                        gteFieldUri = propertyUri.FieldURI;
                    }
                }
            }
        }
    }

    return {
        lteFieldValue,
        lteFieldUri,
        gteFieldValue,
        gteFieldUri,
    };
}

function convertFieldUriToRangeFilterName(fieldUri: string) {
    switch (fieldUri) {
        case 'StartTime':
            return 'StartTime';
        case 'EndTime':
            return 'EndTime';
        case 'DateTimeReceived':
        default:
            return 'received';
    }
}
